import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search as SearchIcon, ChevronDown, XIcon } from 'lucide-react';
import { PARTIES, SETS, TYPES, RESPONSE_TYPES, WORKFLOW_STATUSES, ResponseType, WorkflowStatus } from '../data';
interface FilterSidebarProps {
  selectedParties: string[];
  setSelectedParties: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSets: string[];
  setSelectedSets: React.Dispatch<React.SetStateAction<string[]>>;
  selectedTypes: string[];
  setSelectedTypes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedResponseTypes: ResponseType[];
  setSelectedResponseTypes: React.Dispatch<React.SetStateAction<ResponseType[]>>;
  selectedWorkflowStatuses: WorkflowStatus[];
  setSelectedWorkflowStatuses: React.Dispatch<React.SetStateAction<WorkflowStatus[]>>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}
interface SectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}
function Section({
  title,
  children,
  defaultOpen = true
}: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return <div className="py-4" data-id="element-155">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-xs font-semibold uppercase tracking-wider text-geniac-muted hover:text-geniac-text transition-colors mb-3" data-id="element-156">
        <span data-id="element-157">{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} data-id="element-158" />
      </button>
      {open && children}
    </div>;
}
function CheckboxItem({
  label,
  checked,
  onChange,
  color
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  color?: string;
}) {
  return <label className="flex items-center gap-3 py-1 cursor-pointer group" data-id="element-159">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-4 h-4 border-geniac-border bg-geniac-panel rounded text-geniac-accent focus:ring-geniac-accent focus:ring-offset-0 cursor-pointer" data-id="element-160" />
      {color && <span className={`w-2 h-2 rounded-full ${color}`} data-id="element-161" />}
      <span className="text-sm text-geniac-text group-hover:text-white transition-colors" data-id="element-162">
        {label}
      </span>
    </label>;
}
const partyColors = ['bg-blue-400', 'bg-emerald-400', 'bg-amber-400', 'bg-purple-400'];
const responseTypeColors: Record<string, string> = {
  Substantive: 'bg-emerald-400',
  'Objection Only': 'bg-rose-400',
  'References Documents': 'bg-blue-400'
};
const workflowStatusColors: Record<string, string> = {
  Current: 'bg-emerald-400',
  Amended: 'bg-amber-400',
  'Not Yet Received': 'bg-slate-400'
};
export function FilterSidebar({
  selectedParties,
  setSelectedParties,
  selectedSets,
  setSelectedSets,
  selectedTypes,
  setSelectedTypes,
  selectedResponseTypes,
  setSelectedResponseTypes,
  selectedWorkflowStatuses,
  setSelectedWorkflowStatuses,
  searchQuery,
  setSearchQuery,
  onReset,
  hasActiveFilters
}: FilterSidebarProps) {
  const handlePartyChange = (option: string, checked: boolean) => {
    setSelectedParties(prev => checked ? [...prev, option] : prev.filter(p => p !== option));
  };
  const handleSetChange = (option: string, checked: boolean) => {
    setSelectedSets(prev => checked ? [...prev, option] : prev.filter(s => s !== option));
  };
  const handleTypeChange = (option: string, checked: boolean) => {
    setSelectedTypes(prev => checked ? [...prev, option] : prev.filter(t => t !== option));
  };
  const handleResponseTypeChange = (option: string, checked: boolean) => {
    const type = option as ResponseType;
    setSelectedResponseTypes(prev => checked ? [...prev, type] : prev.filter(t => t !== type));
  };
  const handleWorkflowStatusChange = (option: string, checked: boolean) => {
    const status = option as WorkflowStatus;
    setSelectedWorkflowStatuses(prev => checked ? [...prev, status] : prev.filter(s => s !== status));
  };
  return <motion.div initial={{
    width: 0,
    opacity: 0
  }} animate={{
    width: 260,
    opacity: 1
  }} exit={{
    width: 0,
    opacity: 0
  }} transition={{
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.25
  }} className="h-full border-r border-geniac-border bg-geniac-bg flex-shrink-0 overflow-hidden" data-id="element-163">
      <div className="w-[260px] h-full flex flex-col" data-id="element-164">
        {/* Search */}
        <div className="px-4 pt-4 pb-2" data-id="element-165">
          <div className="relative" data-id="element-166">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-167">
              <SearchIcon className="h-4 w-4 text-geniac-muted" data-id="element-168" />
            </div>
            <input type="text" className="w-full pl-9 pr-8 py-2 border border-geniac-border rounded-md text-sm bg-geniac-panel text-geniac-text placeholder-geniac-muted focus:outline-none focus:ring-1 focus:ring-geniac-accent focus:border-geniac-accent" placeholder="Search documents..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-169" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center text-geniac-muted hover:text-geniac-text" data-id="element-170">
                <XIcon className="w-3.5 h-3.5" data-id="element-171" />
              </button>}
          </div>
        </div>

        {/* Filter Sections — scrollable */}
        <div className="flex-1 overflow-y-auto px-4 divide-y divide-geniac-border" data-id="element-172">
          <Section title="Parties" data-id="element-173">
            <div className="space-y-1" data-id="element-174">
              {PARTIES.map((p, i) => <CheckboxItem key={p} label={p} checked={selectedParties.includes(p)} onChange={checked => handlePartyChange(p, checked)} color={partyColors[i % partyColors.length]} data-id="element-175" />)}
            </div>
          </Section>

          <Section title="Round" data-id="element-176">
            <div className="space-y-1" data-id="element-177">
              {SETS.map(s => <CheckboxItem key={s} label={s} checked={selectedSets.includes(s)} onChange={checked => handleSetChange(s, checked)} data-id="element-178" />)}
            </div>
          </Section>

          <Section title="Discovery Type" data-id="element-179">
            <div className="space-y-1" data-id="element-180">
              {TYPES.map(t => <CheckboxItem key={t} label={t} checked={selectedTypes.includes(t)} onChange={checked => handleTypeChange(t, checked)} data-id="element-181" />)}
            </div>
          </Section>

          <Section title="Response Type" data-id="element-182">
            <div className="space-y-1" data-id="element-183">
              {RESPONSE_TYPES.map(t => <CheckboxItem key={t} label={t} checked={selectedResponseTypes.includes(t)} onChange={checked => handleResponseTypeChange(t, checked)} color={responseTypeColors[t]} data-id="element-184" />)}
            </div>
          </Section>

          <Section title="Status" data-id="element-185">
            <div className="space-y-1" data-id="element-186">
              {WORKFLOW_STATUSES.map(s => <CheckboxItem key={s} label={s} checked={selectedWorkflowStatuses.includes(s)} onChange={checked => handleWorkflowStatusChange(s, checked)} color={workflowStatusColors[s]} data-id="element-187" />)}
            </div>
          </Section>
        </div>

        {/* Clear All — always visible at bottom */}
        <div className={`px-4 py-4 border-t border-geniac-border flex-shrink-0 ${hasActiveFilters ? '' : 'opacity-0 pointer-events-none'}`} data-id="element-188">
          <button onClick={onReset} className="text-sm font-medium text-geniac-accent hover:text-geniac-accent/80 transition-colors" data-id="element-189">
            Clear All Filters
          </button>
        </div>
      </div>
    </motion.div>;
}